import { PlatformError } from "./client";

/* KYC, the way the whitelabel does it (react-sdk-v2 useKyc and batman's
   Kyc* pieces), kept pure so it can be tested without a session.

   The platform runs identity verification with the site's configured
   provider. `/whoami` states where the player stands: `kycEnabled` (the
   site runs KYC at all), `kycLevel` against `requiredKycLevel` (the
   obligation is a comparison, not a flag), and `pendingVerification` while a
   hosted session is open or being decided. `POST /kyc/initiate` opens that
   session and answers the provider's URL; the player comes back to
   `/kyc/result`, which polls `/whoami` until the decision lands. */

export const KYC_LEVEL_IDENTITY_VERIFIED = 2;

export type KycFields = {
  kycEnabled?: boolean;
  kycLevel?: number;
  requiredKycLevel?: number;
  pendingVerification?: boolean;
};

export type KycView = {
  kycEnabled: boolean;
  kycRequired: boolean;
  isVerified: boolean;
  pendingVerification: boolean;
};

const KYC_OFF: KycView = { kycEnabled: false, kycRequired: false, isVerified: false, pendingVerification: false };

/* Every flag is false when KYC is off for the site, when there is no
   session, or when `/whoami` predates the fields, so callers can suppress all
   KYC interface on a single check. */
export const kycView = (identity: KycFields | null | undefined): KycView => {
  if (!identity || identity.kycEnabled !== true) {
    return KYC_OFF;
  }

  const level = identity.kycLevel ?? 0;

  return {
    kycEnabled: true,
    kycRequired: level < (identity.requiredKycLevel ?? 0),
    isVerified: level >= KYC_LEVEL_IDENTITY_VERIFIED,
    pendingVerification: identity.pendingVerification === true,
  };
};

export const KYC_INITIATE_FAILURES = [
  "ALREADY_VERIFIED",
  "VERIFICATION_ALREADY_PENDING",
  "PROFILE_INCOMPLETE",
  "KYC_NOT_CONFIGURED",
  "REDIRECT_URL_NOT_ALLOWED",
] as const;

export type KycInitiateFailure = (typeof KYC_INITIATE_FAILURES)[number];

/* `POST /kyc/initiate` refuses with `details.reason` as its stable
   discriminator (400 and 409). A validation error carries schema issues in
   `details` instead, and anything else carries no reason: both are
   undefined here, and the caller says something generic. */
export const kycInitiateFailureReason = (error: unknown): KycInitiateFailure | undefined => {
  if (!(error instanceof PlatformError)) {
    return undefined;
  }

  const { details } = error;

  if (!details || typeof details !== "object" || Array.isArray(details) || !("reason" in details)) {
    return undefined;
  }

  const reason = (details as { reason: unknown }).reason;

  return KYC_INITIATE_FAILURES.find((known) => known === reason);
};

/* A withdrawal the platform refuses until the player is verified. */
export const isKycRequiredError = (error: unknown) =>
  error instanceof PlatformError && error.message === "KYC_REQUIRED";

type KycProfile = {
  firstName: string | null;
  lastName: string | null;
  countryId: string | null;
  birthDate: string | null;
};

/* The profile fields the provider needs before a session can open. */
export const isKycProfileComplete = <Profile extends KycProfile>(
  profile: Profile | null | undefined,
): profile is Profile =>
  Boolean(
    profile &&
      profile.firstName &&
      profile.lastName &&
      profile.countryId &&
      profile.birthDate &&
      !Number.isNaN(Date.parse(profile.birthDate)),
  );

/* The details, and an email address as well. */
export const isReadyForKycVerification = (profile: (KycProfile & { email: string | null }) | null | undefined) =>
  isKycProfileComplete(profile) && profile.email !== null && profile.email !== "";

/* The provider's URL is followed with a full navigation, so only an https
   URL the browser can parse is ever followed. */
export const toSecureUrl = (value: string | null | undefined) => {
  if (!value) {
    return undefined;
  }

  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.href : undefined;
  } catch {
    return undefined;
  }
};
